import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import * as Query from '../../../api/gql/query'
import { formatMoney, isNegativeMoney } from '../lib/money'
import { monthLabel } from '../lib/overview'
import type { CostBillingByMonth } from '../types'
import BillingByMonthCardSkeleton from './BillingByMonthCardSkeleton'

interface Props { projectId: string; currency: string; locale: string; language: string }

const STICKY = 'sticky left-0 z-10 bg-card'

/** Investor billing spec §5: investor vs. subcontractor billing per month, with the difference and its running total. */
const BillingByMonthCard: React.FC<Props> = ({ projectId, currency, locale, language }) => {
  const { t } = useTranslation()
  const { data, loading } = useQuery<{ costBillingByMonth: CostBillingByMonth | null }>(Query.COST_BILLING_BY_MONTH, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const view = data?.costBillingByMonth
  if (!view) return loading ? <BillingByMonthCardSkeleton /> : null
  if (view.months.length === 0) return null

  const money = (v: string) => formatMoney(v, { locale, currency, whole: true })
  const cell = (v: string, signed = false) => (
    <TableCell className={cn('text-right tabular-nums', signed && isNegativeMoney(v) && 'text-destructive')}>{money(v)}</TableCell>
  )
  const total = (v: string, signed = false) => (
    <TableCell className={cn('text-right font-medium tabular-nums', signed && isNegativeMoney(v) && 'text-destructive')}>{money(v)}</TableCell>
  )
  const rowHead = (label: React.ReactNode, bold = false) => <TableCell className={cn(STICKY, 'whitespace-nowrap', bold && 'font-medium')}>{label}</TableCell>

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{t('CostControl_billing_by_month_title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={STICKY} />
                {view.months.map((m) => <TableHead key={m} className="whitespace-nowrap text-right">{monthLabel(m, language)}</TableHead>)}
                <TableHead className="text-right">{t('Common_total')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                {rowHead(t('CostControl_billing_row_investor'), true)}
                {view.investor.amounts.map((a, i) => (
                  <TableCell key={view.months[i]} className="whitespace-nowrap text-right tabular-nums">
                    {view.investorUninvoiced[i] && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0} role="img" aria-label={t('CostControl_billing_uninvoiced')} className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground align-middle" />
                        </TooltipTrigger>
                        <TooltipContent>{t('CostControl_billing_uninvoiced')}</TooltipContent>
                      </Tooltip>
                    )}
                    {money(a)}
                  </TableCell>
                ))}
                {total(view.investor.total)}
              </TableRow>
              {view.contracts.map((c) => (
                <TableRow key={c.id}>
                  {rowHead(
                    <>
                      <Link className="text-primary hover:underline" to={`/project/${projectId}/cost-control/contracts/${c.id}`}>
                        {c.number ? `${c.number} · ${c.name}` : c.name}
                      </Link>
                      <div className="text-xs text-muted-foreground">{c.counterpartyName}</div>
                    </>,
                  )}
                  {c.amounts.map((a, i) => <React.Fragment key={view.months[i]}>{cell(a)}</React.Fragment>)}
                  {total(c.total)}
                </TableRow>
              ))}
              <TableRow>
                {rowHead(t('CostControl_billing_row_subcontractors'), true)}
                {view.subcontractors.amounts.map((a, i) => <React.Fragment key={view.months[i]}>{cell(a)}</React.Fragment>)}
                {total(view.subcontractors.total)}
              </TableRow>
              <TableRow>
                {rowHead(t('CostControl_billing_row_difference'), true)}
                {view.difference.amounts.map((a, i) => <React.Fragment key={view.months[i]}>{cell(a, true)}</React.Fragment>)}
                {total(view.difference.total, true)}
              </TableRow>
              <TableRow>
                {rowHead(t('CostControl_billing_row_cumulative'), true)}
                {view.cumulative.map((a, i) => <React.Fragment key={view.months[i]}>{cell(a, true)}</React.Fragment>)}
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TooltipProvider>
      </CardContent>
    </Card>
  )
}
export default BillingByMonthCard
